import { useRouter } from 'next/router'
import React, { useMemo, useState, useEffect, useRef } from 'react'
import { Eye, EyeOff } from 'react-feather'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@shopflo/ui'
import { postRequest } from 'src/lib/core/api-client'
import { useLocale } from 'src/lib/hooks/useLocal'
import useClickOutside from 'src/lib/hooks/useClickOutside'
import { apiURI } from 'src/lib/utils/constants'
import { passwordFormSchema, PasswordFormValues, defaultPasswordFormValues } from 'src/@core/schemas/password-schema'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import useSWR from 'swr'
import { fetcher, staticOptions } from 'src/lib/core/api-client'
import { UserDetailsResponse } from 'src/lib/types/onboarding'
import { appendParamsToUrl } from 'src/lib/utils/helpers'

const SetPassword = () => {
  const { t } = useLocale()
  const router = useRouter()

  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
    confirmPassword: false
  })

  const [tooltipOpen, setTooltipOpen] = useState(false)
  const [passwordValue, setPasswordValue] = useState('')
  const [confirmPasswordValue, setConfirmPasswordValue] = useState('')
  const inputContainerRef = useRef<HTMLDivElement>(null)

  const { token } = router.query

  const {
    data: userDetailsResponse,
    error,
    isValidating
  } = useSWR<UserDetailsResponse | null>(
    `/merchant-invitation`,
    async url => {
      if (!token) return null
      const response = (await fetcher(
        appendParamsToUrl(url, { 'invitation-id': token.toString() }),
        apiURI.AUTH_ENGINE
      )) as UserDetailsResponse
      return response
    },
    {
      ...staticOptions,
      onError: err => {
        console.error(err)
        router.push('/auth/login')
      }
    }
  )

  const userDetails = useMemo(() => {
    return {
      email: userDetailsResponse?.data?.oid || '',
      pageExpired: userDetailsResponse?.data?.expired || false
    }
  }, [userDetailsResponse])

  const form = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: defaultPasswordFormValues
  })

  useEffect(() => {
    form.setValue('password', passwordValue)
  }, [passwordValue, form])

  useEffect(() => {
    form.setValue('confirmPassword', confirmPasswordValue)
  }, [confirmPasswordValue, form])

  useClickOutside(inputContainerRef, () => {
    setTooltipOpen(false)
  })

  const togglePasswordVisibility = (field: 'password' | 'confirmPassword') => {
    setPasswordVisibility(prev => ({
      ...prev,
      [field]: !prev[field]
    }))
  }

  const handleCreatePassword = async (data: PasswordFormValues) => {
    const payload = {
      invitation_token: token,
      password: data.password.trim()
    }

    try {
      await postRequest('/merchant-invitation/accept', apiURI.AUTH_ENGINE, payload)
      successToast('Password created successfully')
      router.replace('/auth/login')
    } catch (e: any) {
      console.error(e)
      errorToast(e?.response?.data?.error?.message || t('login_page.link_expired'))
      router.replace('/auth/login')
    }
  }

  if (userDetails.pageExpired) {
    return (
      <div className='flex flex-col  max-w-[380px] gap-8 '>
        <h2 className='text-2xl font-semibold text-zinc-700 items-center text-center'>
          {t('login_page.invite_link_expired')}
        </h2>
        <p className='text-text-sm-regular text-center text-zinc-500'>
          {t('login_page.invite_link_expired_description')}
        </p>
      </div>
    )
  }

  return (
    <div className='flex flex-col gap-16 max-w-[380px]'>
      <div className='flex flex-col gap-0.5'>
        <h2 className='text-2xl font-semibold text-zinc-700 items-center text-center'>{t('set_password')}</h2>
        <p className='text-text-sm-regular text-center text-zinc-500'>{userDetails.email}</p>
      </div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleCreatePassword)}
          className='flex flex-col gap-8 min-w-[380px]'
          data-form-type='other'
          autoComplete='off'
        >
          <FormItem>
            <FormLabel className='text-text-xs-regular text-zinc-500'>{t('enter_password')}</FormLabel>
            <FormControl>
              <div className='relative' ref={inputContainerRef}>
                <Input
                  variant='filled'
                  dimension='lg'
                  type={passwordVisibility.password ? 'text' : 'password'}
                  placeholder={t('password_placeholder')}
                  error={!!form.formState.errors.password}
                  onClick={() => setTooltipOpen(true)}
                  value={passwordValue}
                  onChange={e => setPasswordValue(e.target.value)}
                  autoComplete='off'
                  data-form-type='other'
                  data-lpignore='true'
                  data-1p-ignore='true'
                  name='new-password-field'
                  endAdornment={
                    <button
                      type='button'
                      onClick={() => togglePasswordVisibility('password')}
                      className='flex items-center justify-center'
                    >
                      {passwordVisibility.password ? (
                        <EyeOff className='text-zinc-500' size={16} />
                      ) : (
                        <Eye className='text-zinc-500' size={16} />
                      )}
                    </button>
                  }
                />
                {tooltipOpen && (
                  <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
                    <TooltipTrigger asChild>
                      <div className='absolute inset-0 pointer-events-none' />
                    </TooltipTrigger>
                    <TooltipContent side='right' className='rounded-lg'>
                      <div className='text-text-xs-medium py-2 px-3 flex flex-col gap-1'>
                        <p className='font-medium text-white/70 '>{t('pre_onboarding.password_must_include')}</p>
                        <ul className='flex flex-col gap-1 text-text-xs-medium text-white px-1'>
                          <li>&bull; {t('pre_onboarding.at_least_1_uppercase_letter')}</li>
                          <li>&bull; {t('pre_onboarding.at_least_1_number')}</li>
                          <li>&bull; {t('pre_onboarding.at_least_1_special_character')}</li>
                          <li>&bull; {t('pre_onboarding.minimum_8_characters')}</li>
                        </ul>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </FormControl>
            {form.formState.errors.password && (
              <p className='text-sm text-red-500 mt-1'>{form.formState.errors.password.message}</p>
            )}
          </FormItem>

          <FormItem>
            <FormLabel className='text-text-xs-regular text-zinc-500'>{t('renter_password')}</FormLabel>
            <FormControl>
              <Input
                variant='filled'
                dimension='lg'
                type={passwordVisibility.confirmPassword ? 'text' : 'password'}
                placeholder={t('repassword_placeholder')}
                error={!!form.formState.errors.confirmPassword}
                value={confirmPasswordValue}
                onChange={e => setConfirmPasswordValue(e.target.value)}
                autoComplete='off'
                data-form-type='other'
                data-lpignore='true'
                data-1p-ignore='true'
                name='confirm-new-password-field'
                endAdornment={
                  <button
                    type='button'
                    onClick={() => togglePasswordVisibility('confirmPassword')}
                    className='flex items-center justify-center'
                  >
                    {passwordVisibility.confirmPassword ? (
                      <EyeOff className='text-zinc-500' size={16} />
                    ) : (
                      <Eye className='text-zinc-500' size={16} />
                    )}
                  </button>
                }
              />
            </FormControl>
            {form.formState.errors.confirmPassword && (
              <p className='text-sm text-red-500 mt-1'>{form.formState.errors.confirmPassword.message}</p>
            )}
          </FormItem>

          <Button
            type='submit'
            variant='outline'
            dimension='lg'
            className='w-full h-11 !mt-2'
            disabled={isValidating}
            loading={isValidating}
          >
            {t('continue')}
          </Button>
        </form>
      </Form>
    </div>
  )
}

export default SetPassword
